import { useEffect, useMemo, useRef } from 'react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { moveMenuItemToSection } from 'src/api/moveMenuItemToSection/moveMenuItemToSection';
import { moveItemBetweenSections } from 'src/helpers/moveItemBetweenSections';
import { revertOptimisticWrite } from 'src/helpers/revertOptimisticWrite';
import type { IOptimisticWrite } from 'src/helpers/revertOptimisticWrite';
import type { IDropResult } from 'src/helpers/getDropResult';
import type { IMenu } from 'src/types/menu.types';
import { useGatedMutation } from '../useGatedMutation';
import { useSerialQueue } from '../useSerialQueue';
import type { IEnqueue } from '../useSerialQueue';
import { QueryKeys } from '../QueryKeys';

type IMoveVariables = Extract<IDropResult, { kind: 'move' }>;

type IMoveContext = IOptimisticWrite<IMenu>;

export const useMoveItemToSection = (menuId: string, enqueue?: IEnqueue) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const ownQueue = useSerialQueue();
  const enqueueRequest = enqueue ?? ownQueue;
  const queryKey = useMemo(() => [QueryKeys.menu, menuId], [menuId]);
  const isMenuFetching = useIsFetching({ queryKey }) > 0;
  const hasMoveSettled = useRef(false);

  useEffect(() => {
    if (!isMenuFetching) hasMoveSettled.current = false;
  }, [isMenuFetching]);

  const isSourceCurrent = ({ itemId, fromSectionId }: IMoveVariables): boolean =>
    queryClient
      .getQueryData<IMenu>(queryKey)
      ?.sections.find((section) => section.sectionId === fromSectionId)
      ?.items.some((item) => item.itemId === itemId) ?? false;

  const mutation = useGatedMutation<unknown, Error, IMoveVariables, IMoveContext>({
    isInstructionCurrent: isSourceCurrent,

    mutationFn: ({ itemId, toSectionId, newDisplayOrder }) =>
      enqueueRequest(() => moveMenuItemToSection(menuId, itemId, toSectionId, newDisplayOrder)),

    onMutate: async ({ itemId, fromSectionId, toSectionId, newDisplayOrder }) => {
      const previous = queryClient.getQueryData<IMenu>(queryKey);

      const optimistic = previous
        ? queryClient.setQueryData<IMenu>(queryKey, {
            ...previous,
            sections: moveItemBetweenSections(previous.sections, itemId, fromSectionId, toSectionId, newDisplayOrder),
          })
        : undefined;

      await queryClient.cancelQueries({ queryKey }, { silent: true, revert: false });

      return { previous, optimistic };
    },

    onError: (_error, _variables, context) => {
      revertOptimisticWrite(queryClient, queryKey, context);
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'We have been unable to complete your request',
      });
    },

    onSettled: () => {
      hasMoveSettled.current = true;
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const areDisplayOrdersUnsettled = mutation.isLoading || (hasMoveSettled.current && isMenuFetching);

  return { ...mutation, areDisplayOrdersUnsettled };
};
